import React from 'react';

import { Form, Input } from 'antd';
import { pick } from 'lodash';

import IntlMessages from 'util/IntlMessages';

import ConvertToOptionList from './components/ConvertToOptionList';
import NumberInput from './components/NumberInput';
import OptionListPicker from './components/OptionListPicker';
import SelectInput from './components/SelectInput';
import SelectOptions from './components/SelectOptions';
import SelectSource from './components/SelectSource';
import SwitchInput from './components/SwitchInput';

export const REDUCER_INDEX = 'eventTypes';
export const BASE_URL = 'eventTypes';
export const EVENT_TYPES_LIST_VIEW_STORAGE_KEY = 'eventTypesList';

export const PATHS = {
  BASE_URL,
  CREATE_EVENT_TYPE: `${BASE_URL}/form/create`,
  EDIT_EVENT_TYPE: `${BASE_URL}/form/edit/:formId`,
};

export const getRouteToCreateEventType = () => PATHS.CREATE_EVENT_TYPE;

export const getRouteToEditEventType = formId =>
  PATHS.EDIT_EVENT_TYPE.replace(':formId', formId);

export const MAX_COMPONENTS_PER_EVENT = 20;

// Options are embedded in every copy of the form (triggers, schedules, executions, events),
// so the per-form total keeps those documents well under Firestore's 1 MiB limit.
/** Inline options per selector; longer lists are saved as option lists ("Guardar como lista"). */
export const MAX_OPTIONS_PER_SELECT = 20;

export const DEFAULT_REPRESENTATIVE_ICON = 'assignment';

export const DEFAULT_REPRESENTATIVE_COLOR = { a: 1, b: 216, g: 141, r: 63 };

export const REPRESENTATIVE_COLOR_OPTIONS = [
  { r: 74, g: 20, b: 140 }, { r: 106, g: 27, b: 154 }, { r: 123, g: 31, b: 162 }, { r: 142, g: 36, b: 170 },
  { r: 40, g: 53, b: 147 }, { r: 48, g: 63, b: 159 }, { r: 57, g: 73, b: 171 }, { r: 63, g: 81, b: 181 },
  { r: 13, g: 71, b: 161 }, { r: 21, g: 101, b: 192 }, { r: 25, g: 118, b: 210 }, { r: 63, g: 141, b: 216 },
  { r: 0, g: 96, b: 100 }, { r: 0, g: 131, b: 143 }, { r: 0, g: 137, b: 123 }, { r: 0, g: 150, b: 136 },
  { r: 27, g: 94, b: 32 }, { r: 46, g: 125, b: 50 }, { r: 56, g: 142, b: 60 }, { r: 85, g: 139, b: 47 },
  { r: 121, g: 85, b: 72 }, { r: 141, g: 110, b: 99 }, { r: 166, g: 94, b: 0 }, { r: 178, g: 106, b: 0 },
  { r: 191, g: 54, b: 12 }, { r: 216, g: 67, b: 21 }, { r: 230, g: 81, b: 0 }, { r: 211, g: 84, b: 0 },
  { r: 183, g: 28, b: 28 }, { r: 198, g: 40, b: 40 }, { r: 173, g: 20, b: 87 }, { r: 136, g: 14, b: 79 },
  { r: 38, g: 50, b: 56 }, { r: 55, g: 71, b: 79 }, { r: 69, g: 90, b: 100 }, { r: 84, g: 110, b: 122 },
];

export const COMPONENT_TYPES = {
  // CHECK: 'check',
  FILE: 'file',
  NUMBER: 'number',
  PHOTO: 'photo',
  TAG: 'tag',
  TEXT: 'text',
  TOGGLE: 'toggle',
  VIDEO: 'video',
  SELECT: 'select',
  DATE: 'date',
  SIGNATURE: 'signature',
  // Options live in an organization option list (componentProps.optionListId), not in the form.
  LIST_SELECT: 'listSelect',
};

export const COMPONENT_PROPS = {
  AUTOCOMPLETE: 'autocomplete',
  CHECKED: 'checked',
  MAX_COUNT: 'maxCount',
  MAX_LENGTH: 'maxLength',
  MAX_VALUE: 'max',
  MIN_LENGTH: 'minLength',
  MIN_VALUE: 'min',
  PLACEHOLDER: 'placeholder',
  ORIGIN: 'origin',
  OPTIONS: 'options',
  OPTION_LIST_ID: 'optionListId',
  INCLUDES_TIME: 'includesTime',
  INCLUDE_CLARIFICATION: 'includeClarification',
};

/**
 * Builder-only inputs of a selector field. They never become `componentProps` keys: the source
 * switch is bound to the field's `type`, and the conversion only acts on the options.
 */
const SELECT_INPUTS = {
  SOURCE: 'source',
  CONVERT_TO_LIST: 'convertToList',
};

export const COMMON_PROPS = {
  REQUIRED: 'required',
};

export const TYPES_INTL = {
  // [COMPONENT_TYPES.CHECK]: 'eventTypes.component.check',
  [COMPONENT_TYPES.FILE]: 'eventTypes.component.file',
  [COMPONENT_TYPES.NUMBER]: 'eventTypes.component.number',
  [COMPONENT_TYPES.PHOTO]: 'eventTypes.component.photo',
  [COMPONENT_TYPES.TAG]: 'eventTypes.component.tag',
  [COMPONENT_TYPES.TEXT]: 'eventTypes.component.text',
  [COMPONENT_TYPES.TOGGLE]: 'eventTypes.component.toggle',
  [COMPONENT_TYPES.VIDEO]: 'eventTypes.component.video',
  [COMPONENT_TYPES.SELECT]: 'eventTypes.component.select',
  [COMPONENT_TYPES.DATE]: 'eventTypes.component.date',
  [COMPONENT_TYPES.SIGNATURE]: 'eventTypes.component.signature',
  // One "Selector" for users; the saved-list source is a setting of the field.
  [COMPONENT_TYPES.LIST_SELECT]: 'eventTypes.component.select',
};

export const PROPS_INTL = {
  [COMPONENT_PROPS.AUTOCOMPLETE]: 'eventTypes.component.componentProps.autocomplete',
  [COMPONENT_PROPS.CHECKED]: 'eventTypes.component.componentProps.checked',
  [COMPONENT_PROPS.MAX_COUNT]: 'eventTypes.component.componentProps.maxCount',
  [COMPONENT_PROPS.MAX_LENGTH]: 'eventTypes.component.componentProps.maxLength',
  [COMPONENT_PROPS.MAX_VALUE]: 'eventTypes.component.componentProps.maxValue',
  [COMPONENT_PROPS.MIN_LENGTH]: 'eventTypes.component.componentProps.minLength',
  [COMPONENT_PROPS.MIN_VALUE]: 'eventTypes.component.componentProps.minValue',
  [COMPONENT_PROPS.PLACEHOLDER]: 'eventTypes.component.componentProps.placeholder',
  [COMPONENT_PROPS.ORIGIN]: 'eventTypes.component.componentProps.origin',
  [COMPONENT_PROPS.OPTIONS]: 'eventTypes.component.componentProps.options',
  [COMPONENT_PROPS.OPTION_LIST_ID]: 'eventTypes.component.componentProps.optionListId',
  [COMPONENT_PROPS.INCLUDES_TIME]: 'eventTypes.component.componentProps.includesTime',
  [COMPONENT_PROPS.INCLUDE_CLARIFICATION]: 'eventTypes.component.componentProps.includeClarification',
  [COMMON_PROPS.REQUIRED]: 'eventTypes.component.commonProps.required',
};

const switchInput = (id, fieldKey, extraProps, form, prop) => (
  <SwitchInput
    extraProps={extraProps}
    fieldKey={[fieldKey, prop]}
    form={form}
    key={`${fieldKey}-${prop}`}
    label={PROPS_INTL[prop]}
    name={[`${id}`, 'componentProps', prop]}
  />
);

const numberInput = (id, fieldKey, extraProps, form, prop, options) => (
  <NumberInput
    extraProps={extraProps}
    fieldKey={[fieldKey, prop]}
    form={form}
    key={`${fieldKey}-${prop}`}
    label={PROPS_INTL[prop]}
    name={[`${id}`, 'componentProps', prop]}
    options={{
      ...options,
      // If it has a depName, i add it ot the options
      ...(options.depName ? { dependencyName: [`${id}`, 'componentProps', options.depName] } : {}),
    }}
  />
);

/// ORIGIN
const SELECT_ORIGIN_DEFAULT = 'capture';
const ORIGIN_OPTIONS = {
  capture: 'eventTypes.component.componentProps.origin.capture',
  upload: 'eventTypes.component.componentProps.origin.upload',
  capture_upload: 'eventTypes.component.componentProps.origin.capture_upload',
};
const originIntl = Object.entries(ORIGIN_OPTIONS).reduce((acc, cur) => {
  const key = cur[1];
  const value = cur[0];
  return [
    ...acc,
    {
      label: key,
      value,
    },
  ];
}, []);

const selectOrigin = (id, fieldKey, extraProps, form, prop) => (
  <SelectInput
    extraProps={{
      ...extraProps,
    }}
    fieldKey={[fieldKey, prop]}
    form={form}
    key={`${fieldKey}-${prop}`}
    label={PROPS_INTL[prop]}
    name={[`${id}`, prop]}
    data={originIntl}
    defaultValue={SELECT_ORIGIN_DEFAULT}
  />
);
const selectOptions = (id, fieldKey, form, prop) => {
  const validateInputLength = async (_, value) =>
    new Promise((resolve, reject) => {
      if (value && value.length > 50) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject(<IntlMessages id="eventTypes.component.componentProps.options.inputLength.error" />);
      } else {
        resolve();
      }
    });

  return (
    <SelectOptions
      key={`${fieldKey}-${prop}`} // This is used to avoid warnind relato to keys
      fieldKey={[fieldKey, prop]}
      name={[id, 'componentProps', prop]}
      label={PROPS_INTL[prop]}
      formOptions={[{ name: 'value', required: true, type: 'text' }]}
      limitOptions={MAX_OPTIONS_PER_SELECT}
      form={form}
      isRequired
      extraRules={[{ validator: validateInputLength }]}
    />
  );
};

const PROPS_INPUTS = {
  [COMPONENT_PROPS.CHECKED]: (id, fieldKey, extraProps, form) =>
    switchInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.CHECKED),
  [COMPONENT_PROPS.AUTOCOMPLETE]: (id, fieldKey, extraProps, form) =>
    switchInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.AUTOCOMPLETE),
  [COMPONENT_PROPS.MAX_COUNT]: (id, fieldKey, extraProps, form) =>
    numberInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.MAX_COUNT, {
      min: 1,
      max: 10,
      required: true,
    }),
  [COMPONENT_PROPS.MAX_LENGTH]: (id, fieldKey, extraProps, form) =>
    numberInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.MAX_LENGTH, {
      depName: COMPONENT_PROPS.MIN_LENGTH,
    }),
  [COMPONENT_PROPS.MIN_LENGTH]: (id, fieldKey, extraProps, form) =>
    numberInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.MIN_LENGTH, {
      depName: COMPONENT_PROPS.MAX_LENGTH,
      checkMax: true,
    }),
  [COMPONENT_PROPS.MAX_VALUE]: (id, fieldKey, extraProps, form) =>
    numberInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.MAX_VALUE, {
      depName: COMPONENT_PROPS.MIN_VALUE,
    }),
  [COMPONENT_PROPS.MIN_VALUE]: (id, fieldKey, extraProps, form) =>
    numberInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.MIN_VALUE, {
      depName: COMPONENT_PROPS.MAX_VALUE,
      checkMax: true,
    }),
  [COMPONENT_PROPS.PLACEHOLDER]: (id, fieldKey, extraProps, form) => {
    const name = [id, 'componentProps', COMPONENT_PROPS.PLACEHOLDER];
    const isEditing = form.getFieldValue(['components', ...name]) !== undefined;
    const defaultProps = isEditing ? {} : { initialValue: '' };
    return (
      <Form.Item
        fieldKey={[fieldKey, COMPONENT_PROPS.PLACEHOLDER]}
        key={`${fieldKey}-${COMPONENT_PROPS.PLACEHOLDER}`}
        label={<IntlMessages id={PROPS_INTL[COMPONENT_PROPS.PLACEHOLDER]} />}
        name={name}
        {...defaultProps}
        {...extraProps}
      >
        <Input />
      </Form.Item>
    );
  },
  [COMPONENT_PROPS.ORIGIN]: (id, fieldKey, extraProps, form) =>
    selectOrigin(id, fieldKey, extraProps, form, COMPONENT_PROPS.ORIGIN),
  [COMPONENT_PROPS.OPTIONS]: (id, fieldKey, _, form) =>
    selectOptions(id, fieldKey, form, COMPONENT_PROPS.OPTIONS),
  [COMPONENT_PROPS.OPTION_LIST_ID]: (id, fieldKey, _, form) => (
    <OptionListPicker
      key={`${fieldKey}-${COMPONENT_PROPS.OPTION_LIST_ID}`}
      fieldKey={[fieldKey, COMPONENT_PROPS.OPTION_LIST_ID]}
      form={form}
      label={PROPS_INTL[COMPONENT_PROPS.OPTION_LIST_ID]}
      name={[id, 'componentProps', COMPONENT_PROPS.OPTION_LIST_ID]}
    />
  ),
  [COMPONENT_PROPS.INCLUDES_TIME]: (id, fieldKey, extraProps, form) =>
    switchInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.INCLUDES_TIME),
  [COMPONENT_PROPS.INCLUDE_CLARIFICATION]: (id, fieldKey, extraProps, form) =>
    switchInput(id, fieldKey, extraProps, form, COMPONENT_PROPS.INCLUDE_CLARIFICATION),
  [SELECT_INPUTS.SOURCE]: (id, fieldKey, _, form) => (
    <SelectSource key={`${fieldKey}-${SELECT_INPUTS.SOURCE}`} fieldKey={fieldKey} form={form} id={id} />
  ),
  [SELECT_INPUTS.CONVERT_TO_LIST]: (id, fieldKey, _, form) => (
    <ConvertToOptionList
      key={`${fieldKey}-${SELECT_INPUTS.CONVERT_TO_LIST}`}
      form={form}
      id={id}
      limitOptions={MAX_OPTIONS_PER_SELECT}
    />
  ),
};

export const AVAILABLE_PROPS = {
  // [COMPONENT_TYPES.CHECK]: pick(PROPS_INPUTS, [COMPONENT_PROPS.CHECKED]),
  [COMPONENT_TYPES.FILE]: pick(PROPS_INPUTS, [COMPONENT_PROPS.MAX_COUNT]),
  [COMPONENT_TYPES.NUMBER]: pick(PROPS_INPUTS, [
    COMPONENT_PROPS.MIN_VALUE,
    COMPONENT_PROPS.MAX_VALUE,
    COMPONENT_PROPS.PLACEHOLDER,
  ]),
  [COMPONENT_TYPES.PHOTO]: pick(PROPS_INPUTS, [COMPONENT_PROPS.MAX_COUNT, COMPONENT_PROPS.ORIGIN]),
  [COMPONENT_TYPES.TAG]: pick(PROPS_INPUTS, [
    COMPONENT_PROPS.MIN_LENGTH,
    COMPONENT_PROPS.MAX_LENGTH,
    COMPONENT_PROPS.MAX_COUNT,
  ]),
  [COMPONENT_TYPES.TEXT]: pick(PROPS_INPUTS, [
    COMPONENT_PROPS.AUTOCOMPLETE,
    COMPONENT_PROPS.MIN_LENGTH,
    COMPONENT_PROPS.MAX_LENGTH,
    COMPONENT_PROPS.PLACEHOLDER,
  ]),
  [COMPONENT_TYPES.TOGGLE]: pick(PROPS_INPUTS, [COMPONENT_PROPS.CHECKED]),
  [COMPONENT_TYPES.VIDEO]: pick(PROPS_INPUTS, [COMPONENT_PROPS.MAX_COUNT, COMPONENT_PROPS.ORIGIN]),
  [COMPONENT_TYPES.SELECT]: pick(PROPS_INPUTS, [
    SELECT_INPUTS.SOURCE,
    COMPONENT_PROPS.OPTIONS,
    SELECT_INPUTS.CONVERT_TO_LIST,
  ]),
  [COMPONENT_TYPES.DATE]: pick(PROPS_INPUTS, [COMPONENT_PROPS.INCLUDES_TIME]),
  [COMPONENT_TYPES.SIGNATURE]: pick(PROPS_INPUTS, [COMPONENT_PROPS.INCLUDE_CLARIFICATION]),
  [COMPONENT_TYPES.LIST_SELECT]: pick(PROPS_INPUTS, [SELECT_INPUTS.SOURCE, COMPONENT_PROPS.OPTION_LIST_ID]),
};
